import { Layout } from "@/components/Layout";
import { Helmet } from "react-helmet-async";
import aboutLouise from "@/assets/about-louise.jpg";

const AboutUs = () => {
  return (
    <Layout>
      <Helmet>
        <title>About HIDNstock — The B2B Marketplace for Circular Fashion</title>
        <meta name="description" content="HIDNstock connects fashion brands, sorting facilities and redesigners to turn deadstock and sorted secondhand textiles into new value." />
        <link rel="canonical" href="https://hidnstock.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://hidnstock.com/about" />
        <meta property="og:title" content="About HIDNstock — The B2B Marketplace for Circular Fashion" />
        <meta property="og:description" content="Meet the team building the B2B marketplace for deadstock, sorted secondhand textiles and remake projects." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "url": "https://hidnstock.com/about",
          "name": "About HIDNstock",
          "description": "HIDNstock connects fashion brands, sorting facilities and redesigners through sorted secondhand, deadstock materials and remake projects.",
          "mainEntity": { "@id": "https://hidnstock.com/#organization" }
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="py-20 md:py-28 border-b-4 border-foreground bg-sand bg-grain">
          <div className="container mx-auto px-6 lg:px-8 max-w-5xl">
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-foreground/60 mb-4">
              About HIDNstock
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold uppercase text-foreground">
              Our story
            </h1>
          </div>
        </section>

        {/* Founder letter */}
        <section className="py-20 md:py-28 border-b-4 border-foreground">
          <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
            <div className="grid gap-12 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-16 items-start">
              {/* Portrait */}
              <figure className="lg:sticky lg:top-28">
                <div className="border-4 border-foreground overflow-hidden bg-sand-deep">
                  <img
                    src={aboutLouise}
                    alt="Louise Wintzell, Founder and CEO of HIDNstock"
                    className="w-full h-auto object-cover object-top"
                    loading="eager"
                  />
                </div>
                <figcaption className="mt-4">
                  <p className="font-bold uppercase text-foreground">Louise Wintzell</p>
                  <p className="text-sm text-foreground/60 uppercase">Founder &amp; CEO, HIDNstock</p>
                </figcaption>
              </figure>

              {/* Letter */}
              <div className="space-y-6">
                <p className="text-2xl md:text-3xl font-bold text-foreground leading-snug">
                  I was our first customer.
                </p>
                <p className="text-lg text-foreground/80 leading-relaxed">
                  I wanted to build a brand from products that already existed. After almost 20 years in the textile industry, I knew how to make a product. What I couldn't find was the "material".
                </p>
                <p className="text-lg text-foreground/80 leading-relaxed">
                  Most of the bulk secondhand I found in Europe had been sorted in Pakistan, even when it was sold as "European vintage". The problem had just moved sideways.
                </p>
                <p className="text-lg text-foreground/80 leading-relaxed">
                  More than 300 meetings later, the pattern was clear. Brands paid to get rid of surplus. Sorting facilities couldn't reach the right buyers. Remakers spent more time hunting for materials than making product.
                </p>
                <p className="text-lg text-foreground/80 leading-relaxed">
                  So I stopped looking for a supplier and built what I couldn't find, together with partners who know the textile industry from the inside. My co-owner has deep experience in tech, AI and the startup world, and also knows the fashion industry. He has been invaluable in building the platform, including the AI that makes anonymous yet traceable trade possible.
                </p>
                <p className="text-lg text-foreground/80 leading-relaxed">
                  We verify every member, so a seller can stay anonymous to the public, but never to us. And we'd never let a seller on board that we wouldn't buy from ourselves. Every lot carries traceable proof.
                </p>
                <p className="text-lg text-foreground/80 leading-relaxed">
                  Deadstock or secondhand, the solution to excess is the same, and it's called creativity.
                </p>

                <div className="pt-6 border-t-4 border-foreground">
                  <p className="font-bold uppercase text-foreground text-xl">HIDNstock</p>
                  <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-foreground/60 mt-1">
                    Where excess meets creativity
                  </p>
                  <p className="mt-6 text-foreground/80 italic">Louise Wintzell, Founder &amp; CEO</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default AboutUs;
